/**
 * The eleven S&P 500 sectors and their SPDR select-sector ETFs.
 *
 * One list, shared by Sector Rotation and Options Breadth. They sit on the same
 * page, so a sector named "Consumer Cyclical" in one panel and "Consumer
 * Discretionary" in the other reads as two different things when it is one.
 *
 * Naming follows GICS, which is what Sector Rotation already displayed. Feeds
 * that emit Morningstar wording are normalised through SECTOR_ETF_BY_NAME.
 */

export interface Sector {
  sym: string;
  name: string;
  /** Approximate S&P 500 weight. Drives bubble size in the rotation scatter. */
  weight: number;
}

// Weights and ordering carried over from the Duck Terminal rotation.py.
export const SECTORS: Sector[] = [
  { sym: 'XLK', name: 'Technology', weight: 0.310 },
  { sym: 'XLF', name: 'Financials', weight: 0.130 },
  { sym: 'XLV', name: 'Health Care', weight: 0.120 },
  { sym: 'XLY', name: 'Consumer Discretionary', weight: 0.100 },
  { sym: 'XLC', name: 'Communication Services', weight: 0.090 },
  { sym: 'XLI', name: 'Industrials', weight: 0.090 },
  { sym: 'XLP', name: 'Consumer Staples', weight: 0.060 },
  { sym: 'XLE', name: 'Energy', weight: 0.040 },
  { sym: 'XLU', name: 'Utilities', weight: 0.025 },
  { sym: 'XLB', name: 'Materials', weight: 0.025 },
  { sym: 'XLRE', name: 'Real Estate', weight: 0.025 },
];

/** Canonical display name for an ETF ticker. */
export const SECTOR_NAME_BY_ETF: Record<string, string> = Object.fromEntries(
  SECTORS.map((s) => [s.sym, s.name])
);

/**
 * ETF for a sector name, keyed by every spelling we expect to receive.
 *
 * The options feed follows Morningstar wording — "Consumer Cyclical" not
 * "Consumer Discretionary", "Financial Services" not "Financials". Both are
 * mapped, so the ETF resolves either way and the display name can then be
 * looked up from it.
 */
export const SECTOR_ETF_BY_NAME: Record<string, string> = {
  Technology: 'XLK',
  'Communication Services': 'XLC',
  'Consumer Cyclical': 'XLY',
  'Consumer Discretionary': 'XLY',
  'Consumer Defensive': 'XLP',
  'Consumer Staples': 'XLP',
  Energy: 'XLE',
  'Financial Services': 'XLF',
  Financials: 'XLF',
  Healthcare: 'XLV',
  'Health Care': 'XLV',
  Industrials: 'XLI',
  'Basic Materials': 'XLB',
  Materials: 'XLB',
  'Real Estate': 'XLRE',
  Utilities: 'XLU',
};

/** ETF for a feed-supplied sector name, or undefined if unrecognised. */
export function sectorEtf(name: string, given?: string): string | undefined {
  return given ?? SECTOR_ETF_BY_NAME[name];
}

/**
 * The name this app shows for a sector, whatever the feed called it.
 *
 * Falls through to the raw name when the sector is one we do not map — better
 * an unfamiliar label than a blank row.
 */
export function sectorName(name: string, given?: string): string {
  const etf = sectorEtf(name, given);
  return (etf && SECTOR_NAME_BY_ETF[etf]) || name;
}
